import { useState, useEffect, useRef, useCallback } from 'react'
import { reportsAPI } from '../services/api'

const FILE_ICONS = {
  pdf: 'bi-file-earmark-pdf-fill text-red-500',
  doc: 'bi-file-earmark-word-fill text-blue-500',
  docx: 'bi-file-earmark-word-fill text-blue-500',
  xls: 'bi-file-earmark-excel-fill text-green-600',
  xlsx: 'bi-file-earmark-excel-fill text-green-600',
  ppt: 'bi-file-earmark-ppt-fill text-orange-500',
  pptx: 'bi-file-earmark-ppt-fill text-orange-500',
  zip: 'bi-file-earmark-zip-fill text-yellow-600',
  csv: 'bi-file-earmark-spreadsheet-fill text-green-600',
  txt: 'bi-file-earmark-text-fill text-gray-500',
}
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'])
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip'

const extensionOf = (name = '') => (name.includes('.') ? name.split('.').pop().toLowerCase() : '')

const iconFor = (name) => {
  const ext = extensionOf(name)
  if (IMAGE_EXTENSIONS.has(ext)) return 'bi-file-earmark-image-fill text-purple-500'
  return FILE_ICONS[ext] || 'bi-file-earmark-fill text-gray-400'
}

const formatSize = (bytes) => {
  if (!bytes) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(i > 0 && n < 10 ? 1 : 0)} ${units[i]}`
}

/**
 * Per-survey report upload/download panel. Whoever can access the survey can see
 * this list; only Admin/Manager (canUpload) can add or remove files - everyone
 * else gets a read-only, download-only view of the same list.
 */
const SurveyReportsModal = ({ referenceNumber, canUpload, onClose, onChanged }) => {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await reportsAPI.list(referenceNumber)
      setReports(res.data.data || [])
    } catch (err) {
      setError('Failed to load reports.')
    } finally {
      setLoading(false)
    }
  }, [referenceNumber])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    setUploadError(null)
    setUploading(true)
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      await reportsAPI.upload(referenceNumber, formData)
      await load()
      onChanged?.()
    } catch (err) {
      const errData = err.response?.data?.errors || err.response?.data?.message
      setUploadError(Array.isArray(errData) ? errData.join(', ') : (errData || 'Upload failed. Please try again.'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return
    try {
      await reportsAPI.delete(referenceNumber, id)
      await load()
      onChanged?.()
    } catch (err) {
      alert('Failed to delete file.')
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    if (canUpload) setDragActive(true)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    if (canUpload) doUpload(e.dataTransfer.files)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="survey-reports-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="table-head-premium flex items-center justify-between px-5 py-3">
          <div className="min-w-0">
            <h3 id="survey-reports-title" className="flex items-center gap-2 text-sm font-semibold text-white">
              <i className="bi bi-folder2-open"></i>
              Survey Report
            </h3>
            <p className="mt-0.5 text-xs text-white/80">{referenceNumber}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white hover:bg-white/20"
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          {canUpload && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                dragActive
                  ? 'border-[#0a5fe0] bg-[#eef4ff]'
                  : 'border-[#d9e2f1] hover:border-[#0a5fe0] hover:bg-[#f8faff]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_EXTENSIONS}
                className="hidden"
                onChange={(e) => doUpload(e.target.files)}
              />
              <span className="badge-metallic mx-auto h-10 w-10 text-lg">
                <i className={`bi ${uploading ? 'bi-hourglass-split' : 'bi-cloud-upload'}`}></i>
              </span>
              <p className="mt-3 text-sm font-medium text-slate-700">
                {uploading ? 'Uploading...' : 'Drag & drop files here, or click to browse'}
              </p>
              <p className="mt-1 text-xs text-slate-400">Images, PDF, Office docs, ZIP · up to 25MB each · multiple files allowed</p>
            </div>
          )}
          {uploadError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{uploadError}</p>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">Loading...</div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-red-600">{error}</div>
          ) : reports.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <i className="bi bi-inbox mb-2 block text-3xl text-slate-300"></i>
              No reports uploaded yet.
            </div>
          ) : (
            <ul className="divide-y divide-[#eef2f8] overflow-hidden rounded-xl border border-[#d9e2f1]">
              {reports.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#f8faff]">
                  <i className={`bi ${iconFor(r.original_filename)} flex-shrink-0 text-2xl`}></i>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800" title={r.original_filename}>
                      {r.original_filename}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatSize(r.file_size)} · {r.uploaded_by_username} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <a
                    href={r.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-[#0a5fe0] hover:bg-[#eef4ff]"
                    title="Download"
                  >
                    <i className="bi bi-download"></i>
                  </a>
                  {canUpload && (
                    <button
                      type="button"
                      onClick={() => handleDelete(r.id)}
                      className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default SurveyReportsModal
